import React, { useState } from "react";
import { Input } from "../ui/input";
import { Plus, Trash2, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import RawNumInput from "./RawNumInput";
import { surfaceNet } from "../../lib/calculator";
import { autoSelectOnFocus } from "../../lib/autoNameSelect";

// v6 F42 — Gevelwerken v2: één gevelvlak binnen een buitenzone (multi-surface flow).
// Ondersteunt shape_types: rect / triangle / trapezoid / manual.
// Per-vlak aftrekken (openingen), collapsible header met bruto/netto samenvatting.
const SHAPES = [
  { value: "rect",      label: "Rechthoek",  hint: "L × H" },
  { value: "triangle",  label: "Driehoek",   hint: "(L × H) / 2" },
  { value: "trapezoid", label: "Trapezium",  hint: "L × (H1 + H2) / 2" },
  { value: "manual",    label: "Manueel",    hint: "m² invullen" },
];

// v6 F42b — Aftrek-typekeuze. Voor "other" is de vrije beschrijving verplicht;
// bij Raam/Deur/Poort is de beschrijving optioneel (bv. "Keukenraam").
export const DEDUCTION_KINDS = [
  { value: "window", label: "Raam",  placeholder: "Bv. Keukenraam (optioneel)" },
  { value: "door",   label: "Deur",  placeholder: "Bv. Voordeur (optioneel)"    },
  { value: "gate",   label: "Poort", placeholder: "Bv. Garagepoort (optioneel)" },
  { value: "other",  label: "Ander", placeholder: "Naam / omschrijving"         },
];

// Herbruikbare aftrek-rij (multi-surface + legacy). Backwards-compat: als
// deduction.kind ontbreekt (legacy record) → default "window" bij weergave;
// de vrije name wordt getoond en blijft bewerkbaar zoals voorheen.
export function DeductionRow({ deduction, testidPrefix, onUpdate, onRemove }) {
  const kind = deduction.kind || "window";
  const spec = DEDUCTION_KINDS.find((k) => k.value === kind) || DEDUCTION_KINDS[0];
  const a = (+deduction.length || 0) * (+deduction.height || 0);
  return (
    <div
      className="bg-[#FAFAF7] rounded-lg p-2 border border-[#E5E2DC] space-y-1.5"
      data-testid={testidPrefix}
    >
      {/* Type pills */}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] mr-1">Type:</span>
        <div className="flex gap-1 bg-white rounded-md p-0.5" data-testid={`${testidPrefix}-kind`}>
          {DEDUCTION_KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              onClick={() => onUpdate({ kind: k.value })}
              className={`px-2 h-7 rounded-sm text-[11px] font-medium transition ${kind === k.value ? "bg-[#6B8E6B] text-white shadow" : "text-[#4F4F4D] hover:bg-[#F2F5EE]"}`}
              data-testid={`${testidPrefix}-kind-${k.value}`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center text-[#D9534F]"
          data-testid={`${testidPrefix}-remove`}
          aria-label="Verwijder aftrek"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Description + dims */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={deduction.name || ""}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder={spec.placeholder}
          className="h-8 rounded-md text-sm flex-1 min-w-[140px]"
          data-testid={`${testidPrefix}-name`}
        />
        <div className="flex flex-col items-start">
          <RawNumInput
            value={deduction.length ?? 0}
            onChange={(v) => onUpdate({ length: typeof v === "number" ? v : 0 })}
            className="h-8 w-16 rounded-md text-right font-mono"
            data-testid={`${testidPrefix}-L`}
          />
          <span className="text-[9px] text-[#5D7259] mt-0.5">L (m)</span>
        </div>
        <span className="text-[#A8A7A0] text-xs">×</span>
        <div className="flex flex-col items-start">
          <RawNumInput
            value={deduction.height ?? 0}
            onChange={(v) => onUpdate({ height: typeof v === "number" ? v : 0 })}
            className="h-8 w-16 rounded-md text-right font-mono"
            data-testid={`${testidPrefix}-H`}
          />
          <span className="text-[9px] text-[#5D7259] mt-0.5">H (m)</span>
        </div>
        <div className="text-xs font-mono text-[#4F4F4D] w-20 text-right" data-testid={`${testidPrefix}-area`}>
          = {a.toFixed(2)} m²
        </div>
      </div>
    </div>
  );
}

function DimInput({ value, onChange, label, testid, width = "w-20" }) {
  return (
    <div className="flex flex-col items-start">
      <RawNumInput
        value={value ?? 0}
        onChange={(v) => onChange(typeof v === "number" ? v : 0)}
        className={`h-9 ${width} rounded-lg text-right font-mono`}
        data-testid={testid}
      />
      <span className="text-[10px] text-[#5D7259] mt-0.5">{label}</span>
    </div>
  );
}

export default function FacadeSurfaceRow({
  surface,
  index,
  defaultCollapsed = false,
  onUpdate,
  onRemove,
  zoneId,
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const shape = (surface.shape_type || "rect").toLowerCase();
  const { gross, deduction, net } = surfaceNet(surface);
  const displayName = surface.name?.trim() || `Gevelvlak ${index + 1}`;

  const updShape = (s) => onUpdate({ shape_type: s });

  const addDeduction = () => {
    const next = [
      ...(surface.deductions || []),
      { id: crypto.randomUUID(), kind: "window", name: "", length: 0, height: 0 },
    ];
    onUpdate({ deductions: next });
  };
  const updDeduction = (idx, patch) => {
    const next = (surface.deductions || []).map((d, i) => (i === idx ? { ...d, ...patch } : d));
    onUpdate({ deductions: next });
  };
  const remDeduction = (idx) => {
    const next = (surface.deductions || []).filter((_, i) => i !== idx);
    onUpdate({ deductions: next });
  };

  return (
    <div
      className="rounded-xl bg-white border border-[#6B8E6B]/25 overflow-hidden"
      data-testid={`facade-surface-${zoneId}-${index}`}
    >
      {/* Header — click to toggle */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#F2F5EE] text-left"
        data-testid={`facade-surface-toggle-${zoneId}-${index}`}
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="w-4 h-4 text-[#6B8E6B] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#6B8E6B] flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#2C2C2A] truncate" data-testid={`facade-surface-name-${zoneId}-${index}`}>
            {displayName}
          </div>
          <div className="text-[10px] text-[#5D7259] uppercase tracking-wider">
            {SHAPES.find((s) => s.value === shape)?.label || "Rechthoek"}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-[#5D7259]">Netto</div>
          <div
            className="font-mono font-bold text-[#6B8E6B] text-sm"
            data-testid={`facade-surface-net-${zoneId}-${index}`}
          >
            {net.toFixed(2)} m²
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="w-8 h-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#D9534F] flex-shrink-0"
          data-testid={`facade-surface-remove-${zoneId}-${index}`}
          aria-label="Verwijder gevelvlak"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </button>

      {/* Expanded body */}
      {!collapsed && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-[#6B8E6B]/15">
          {/* Naam */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">
              Naam gevelvlak
            </label>
            <Input
              value={surface.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              onFocus={autoSelectOnFocus}
              placeholder={`Gevelvlak ${index + 1}`}
              className="h-9 rounded-lg text-sm"
              data-testid={`facade-surface-name-input-${zoneId}-${index}`}
            />
          </div>

          {/* Shape selector */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">
              Vorm
            </label>
            <div className="flex flex-wrap gap-1 bg-[#F2F5EE] rounded-lg p-1" data-testid={`facade-surface-shape-${zoneId}-${index}`}>
              {SHAPES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => updShape(s.value)}
                  className={`flex-1 min-w-[70px] px-2 h-8 rounded-md text-xs font-medium transition ${shape === s.value ? "bg-[#6B8E6B] text-white shadow" : "text-[#4F4F4D] hover:bg-white"}`}
                  data-testid={`facade-surface-shape-${s.value}-${zoneId}-${index}`}
                  title={s.hint}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* v14ao — Per-facade AutoFill knop verwijderd. AutoMeasure gebeurt nu op
              zone-niveau (zie AutoMeasureZoneBlock). Individuele gevels blijven
              manueel bewerkbaar. De Sparkles-badge blijft zichtbaar op vlakken
              die door AutoMeasure werden aangemaakt/gevuld. */}
          {surface.automeasure_source && (
            <div className="flex items-center gap-1 text-[10px] text-[#5D7259]" data-testid={`facade-surface-automeasure-badge-${zoneId}-${index}`}>
              <Sparkles className="w-3 h-3" />
              Ingevuld via AutoMeasure — {surface.automeasure_source.provider}
            </div>
          )}

          {/* Dimension inputs */}
          <div className="flex flex-wrap items-end gap-3">
            {shape === "rect" && (
              <>
                <DimInput value={surface.dim_length} onChange={(v) => onUpdate({ dim_length: v })} label="Lengte (m)" testid={`facade-surface-L-${zoneId}-${index}`} />
                <span className="text-[#A8A7A0] pb-3">×</span>
                <DimInput value={surface.dim_height} onChange={(v) => onUpdate({ dim_height: v })} label="Hoogte (m)" testid={`facade-surface-H-${zoneId}-${index}`} />
              </>
            )}
            {shape === "triangle" && (
              <>
                <DimInput value={surface.dim_length} onChange={(v) => onUpdate({ dim_length: v })} label="Basis (m)" testid={`facade-surface-L-${zoneId}-${index}`} />
                <span className="text-[#A8A7A0] pb-3">×</span>
                <DimInput value={surface.dim_height} onChange={(v) => onUpdate({ dim_height: v })} label="Hoogte (m)" testid={`facade-surface-H-${zoneId}-${index}`} />
                <span className="text-[#A8A7A0] pb-3">/ 2</span>
              </>
            )}
            {shape === "trapezoid" && (
              <>
                <DimInput value={surface.dim_length} onChange={(v) => onUpdate({ dim_length: v })} label="Breedte L (m)" testid={`facade-surface-L-${zoneId}-${index}`} />
                <DimInput value={surface.dim_height} onChange={(v) => onUpdate({ dim_height: v })} label="H1 (m)" testid={`facade-surface-H1-${zoneId}-${index}`} />
                <DimInput value={surface.dim_height2} onChange={(v) => onUpdate({ dim_height2: v })} label="H2 (m)" testid={`facade-surface-H2-${zoneId}-${index}`} />
              </>
            )}
            {shape === "manual" && (
              <DimInput
                value={surface.manual_area}
                onChange={(v) => onUpdate({ manual_area: v })}
                label="Oppervlakte (m²)"
                testid={`facade-surface-manual-${zoneId}-${index}`}
                width="w-28"
              />
            )}
            <div className="text-xs text-[#4F4F4D] pb-3 ml-auto">
              Bruto = <strong className="text-[#2C2C2A] font-mono" data-testid={`facade-surface-gross-${zoneId}-${index}`}>{gross.toFixed(2)} m²</strong>
            </div>
          </div>

          {/* Aftrekken */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259]">
                Aftrekken (ramen · deuren · openingen)
              </div>
              <button
                type="button"
                onClick={addDeduction}
                className="text-xs px-2 h-7 rounded-md border border-[#E5E2DC] hover:border-[#6B8E6B] hover:bg-[#F2F5EE] text-[#2C2C2A] font-medium gap-1 inline-flex items-center"
                data-testid={`facade-surface-add-deduction-${zoneId}-${index}`}
              >
                <Plus className="w-3 h-3" /> Aftrek
              </button>
            </div>
            {(surface.deductions || []).length === 0 ? (
              <div className="text-[11px] text-[#5D7259] italic px-1">Geen openingen toegevoegd</div>
            ) : (
              <div className="space-y-1.5">
                {(surface.deductions || []).map((d, idx) => (
                  <DeductionRow
                    key={d.id || idx}
                    deduction={d}
                    testidPrefix={`facade-surface-deduction-${zoneId}-${index}-${idx}`}
                    onUpdate={(patch) => updDeduction(idx, patch)}
                    onRemove={() => remDeduction(idx)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Summary per surface */}
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 border-t border-[#6B8E6B]/15 text-xs"
            data-testid={`facade-surface-summary-${zoneId}-${index}`}
          >
            <span>Bruto: <strong className="font-mono">{gross.toFixed(2)} m²</strong></span>
            <span className="text-[#D9534F]">− Aftrek: <strong className="font-mono">{deduction.toFixed(2)} m²</strong></span>
            <span className="ml-auto text-sm">
              Netto: <strong className="font-mono text-[#6B8E6B]">{net.toFixed(2)} m²</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
